import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useMe } from "@/context/MeContext";
import { errMsg, eur, useApi } from "@/lib/api";
import { PointsSelector, discountFor, maxPointsFor } from "@/components/club/PointsSelector";
import { PaymentMethods } from "@/components/club/PaymentMethods";
import { AddressFields, emptyAddress } from "./AddressFields";

export const SummaryRow = ({ label, value, strong, testid }) => (
  <div className={`flex justify-between ${strong ? "pt-2 text-lg font-bold text-white" : "text-sm text-slate-300"}`}>
    <span>{label}</span>
    <span data-testid={testid}>{value}</span>
  </div>
);

export function FateloVoiDialog({ result, index, usePoints, onClose }) {
  const offer = result.offers[index];
  const { me, refresh } = useMe();
  const api = useApi();
  const navigate = useNavigate();
  const total = offer.total;
  const initMax = me ? maxPointsFor(me.balance, me.redeem_rate, total) : 0;
  const [enabled, setEnabled] = useState(usePoints && initMax > 0);
  const [points, setPoints] = useState(usePoints ? initMax : 0);
  const [address, setAddress] = useState({ ...emptyAddress, full_name: me?.full_name || "" });
  const [phone, setPhone] = useState(me?.phone || "");
  const [busy, setBusy] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const missingFields = !address.full_name.trim() || !address.street.trim() || !address.city.trim() || !address.zip.trim() || phone.trim().length < 6;
  const discount = enabled && me ? discountFor(points, me.redeem_rate, total) : 0;

  const submit = async () => {
    setBusy(true);
    try {
      const order = await api("post", "/orders/fatelo-voi", {
        search_id: result.id, offer_index: index, address, phone, points_to_use: enabled ? points : 0, accept_shipping_terms: accepted,
      });
      await refresh();
      toast.success("Ordine creato! SMS di conferma inviato a te e all'Admin.");
      navigate("/ordine-confermato", { state: { orders: [order] } });
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="fatelo-voi-dialog" className="max-h-[92vh] overflow-y-auto border-white/10 bg-[#12141C] sm:max-w-xl">
        <DialogHeader>
          <DialogTitle className="font-display text-xl text-white">Fatelo voi: compriamo noi per te</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-slate-400">{result.product?.name} da <b className="text-white">{offer.shop}</b>. Lo acquistiamo e te lo spediamo; guadagni <b className="text-amber-300">2 punti per ogni €</b> (al netto IVA).</p>
        <AddressFields value={address} onChange={setAddress} phone={phone} setPhone={setPhone} />
        <div data-testid="shipping-disclaimer" className="rounded-2xl border border-red-500/25 bg-red-500/[0.06] p-4 text-xs leading-relaxed text-slate-300">
          <p className="mb-1 flex items-center gap-1.5 font-semibold text-red-300"><AlertTriangle className="h-4 w-4" /> Verifica i dati di spedizione</p>
          Spediremo il prodotto esattamente all'indirizzo che hai inserito. In caso di dati errati o incompleti (nome, indirizzo, CAP, città, cellulare) il club non è responsabile per mancate consegne, ritardi o smarrimenti: eventuali costi di giacenza, reso o nuova spedizione sono a carico del cliente e l'ordine non può essere rimborsato per errori nei dati forniti.
          <label className="mt-3 flex cursor-pointer items-start gap-2 text-slate-200">
            <Checkbox data-testid="shipping-disclaimer-checkbox" checked={accepted} onCheckedChange={(v) => setAccepted(!!v)} className="mt-0.5" />
            <span>Confermo che i dati di spedizione sono corretti e accetto le condizioni sopra indicate.</span>
          </label>
        </div>
        <PointsSelector me={me} payable={total} enabled={enabled} setEnabled={setEnabled} points={points} setPoints={setPoints} />
        <PaymentMethods />
        <div className="space-y-1 rounded-2xl border border-white/10 p-4">
          <SummaryRow label="Prezzo prodotto" value={eur(offer.price)} />
          <SummaryRow label="Spedizione" value={eur(offer.shipping)} />
          {discount > 0 && <SummaryRow label="Sconto punti" value={`−${eur(discount)}`} testid="summary-discount" />}
          <SummaryRow label="Da pagare con bonifico istantaneo" value={eur(total - discount)} strong testid="summary-amount-due" />
        </div>
        <button data-testid="fatelo-voi-submit" disabled={busy || !accepted || missingFields} onClick={submit} className="flex h-12 items-center justify-center gap-2 rounded-xl bg-amber-500 font-semibold text-black transition-colors hover:bg-amber-400 disabled:opacity-60">
          {busy && <Loader2 className="h-4 w-4 animate-spin" />} Conferma ordine
        </button>
      </DialogContent>
    </Dialog>
  );
}
